import React from "react";
import "./FeedbackProgressPanel.css";

export const FEEDBACK_PROGRESS_STAGES = [
  {
    progress: 10,
    stage: "Submission received",
    subtitle: "Your writing has been submitted successfully.",
    icon: "submission",
  },
  {
    progress: 25,
    stage: "Reading your writing",
    subtitle: "Analyzing your ideas and how you expressed them.",
    icon: "reading",
  },
  {
    progress: 40,
    stage: "Finding writing signals",
    subtitle: "Looking for details, actions, emotions, and craft choices.",
    icon: "signals",
  },
  {
    progress: 55,
    stage: "Checking lesson goals",
    subtitle: "Comparing your response with today’s writing skill.",
    icon: "goals",
  },
  {
    progress: 75,
    stage: "Evaluating your work",
    subtitle: "Reviewing creativity, clarity, and task completion.",
    icon: "evaluate",
  },
  {
    progress: 90,
    stage: "Preparing Coach feedback",
    subtitle: "Coach Shaanzae is preparing your personalized guidance.",
    icon: "coach",
  },
  {
    progress: 100,
    stage: "Feedback ready",
    subtitle: "Your feedback is ready.",
    icon: "ready",
  },
];

function clampProgress(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
}

function resolveStage(progress, stage) {
  const exact = FEEDBACK_PROGRESS_STAGES.find((item) => item.stage === stage);
  if (exact) return exact;
  const current = FEEDBACK_PROGRESS_STAGES
    .slice()
    .reverse()
    .find((item) => clampProgress(progress) >= item.progress);
  return current || FEEDBACK_PROGRESS_STAGES[0];
}

function StepIcon({ kind }) {
  const common = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.9",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };

  switch (kind) {
    case "submission":
      return (
        <svg {...common}>
          <path d="M5 12.5l4.2 4.2L19 7" />
        </svg>
      );
    case "reading":
      return (
        <svg {...common}>
          <path d="M4.5 6.5h6.4c1.2 0 2.1.9 2.1 2.1v10.8c-.6-.6-1.6-1.1-2.8-1.1H4.5z" />
          <path d="M19.5 6.5h-6.4c-1.2 0-2.1.9-2.1 2.1v10.8c.6-.6 1.6-1.1 2.8-1.1h5.7z" />
        </svg>
      );
    case "signals":
      return (
        <svg {...common}>
          <path d="M8 16.5l8-8" />
          <path d="M10 6.5l1.2 2.2L13.5 10l-2.3 1.3L10 13.5l-1.2-2.2L6.5 10l2.3-1.3z" />
        </svg>
      );
    case "goals":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="7.2" />
          <path d="M12 12l3.2-3.2" />
          <path d="M12 4.8v2.2" />
          <path d="M19.2 12h-2.2" />
        </svg>
      );
    case "evaluate":
      return (
        <svg {...common}>
          <path d="M6.5 19.5V13" />
          <path d="M12 19.5V8.5" />
          <path d="M17.5 19.5v-5.2" />
        </svg>
      );
    case "coach":
      return (
        <svg {...common}>
          <path d="M4.8 6.5h14.4v8H9.2l-3.7 3.2V14.5H4.8z" />
          <path d="M8.2 10.1h7.6" />
          <path d="M8.2 12.5h4.4" />
        </svg>
      );
    case "ready":
    default:
      return (
        <svg {...common}>
          <path d="M5.5 4.8v14.4" />
          <path d="M5.5 6.2h7.6l-1.5 2.2 1.5 2.1H5.5" />
          <path d="M12.6 10.4h5.9" />
        </svg>
      );
  }
}

function PanelIcon() {
  return (
    <svg viewBox="0 0 24 24" className="fbp-panel-icon" aria-hidden="true">
      <path d="M12 2l1.4 4.2L18 7.6l-4.2 1.4L12 13l-1.4-4L6 7.6l4.6-1.4z" />
      <path d="M4 11l1 2.8L8 14.8l-3 1.1L4 19l-1-3.1-3-1.1 3-1.1z" />
      <path d="M18 12l.9 2.4 2.6.9-2.6.9L18 19l-.9-2.8-2.6-.9 2.6-.9z" />
    </svg>
  );
}

export default function FeedbackProgressPanel({ progress, stage, subtitle, status }) {
  const resolved = resolveStage(progress, stage);
  const progressValue = clampProgress(progress || resolved.progress);
  const activeIndex = progressValue >= 100
    ? FEEDBACK_PROGRESS_STAGES.length - 1
    : FEEDBACK_PROGRESS_STAGES.reduce(
        (idx, item, index) => (progressValue >= item.progress ? index : idx),
        0
      );
  const isDone = String(status || "") === "completed" || progressValue >= 100;

  return (
    <section className={`fbp-card ${isDone ? "is-done" : ""}`} aria-live="polite" aria-label="Feedback progress">
      <div className="fbp-top-row">
        <div className="fbp-title-wrap">
          <div className="fbp-badge">
            <PanelIcon />
          </div>
          <div>
            <div className="fbp-title">{resolved.stage}</div>
            <div className="fbp-subtitle">{subtitle || resolved.subtitle}</div>
          </div>
        </div>
        <div className="fbp-percent">{progressValue}% complete</div>
      </div>

      <div className="fbp-track" aria-hidden="true">
        <div className="fbp-fill" style={{ width: `${progressValue}%` }} />
      </div>

      <div className="fbp-timeline" aria-hidden="true">
        {FEEDBACK_PROGRESS_STAGES.map((item, index) => {
          const state =
            progressValue >= 100 || index < activeIndex
              ? "complete"
              : index === activeIndex
              ? "current"
              : "future";
          const labelState = state === "future" ? "muted" : state === "current" ? "current" : "complete";
          return (
            <React.Fragment key={item.stage}>
              <div className={`fbp-step ${state}`}>
                <div className="fbp-step-icon-wrap">
                  <div className="fbp-step-icon">
                    {state === "complete" ? (
                      <svg viewBox="0 0 24 24" className="fbp-check" aria-hidden="true">
                        <path d="M5 12.5l4.2 4.2L19 7" />
                      </svg>
                    ) : (
                      <StepIcon kind={item.icon} />
                    )}
                  </div>
                </div>
                <div className={`fbp-step-label ${labelState}`}>{item.stage}</div>
              </div>
              {index < FEEDBACK_PROGRESS_STAGES.length - 1 ? (
                <div className={`fbp-connector ${index < activeIndex || progressValue >= 100 ? "complete" : ""}`} />
              ) : null}
            </React.Fragment>
          );
        })}
      </div>

    </section>
  );
}
